/** The import lifecycle values the API actually sends. There is no separate
 *  "completed with errors" value, so a partial import is Completed with a
 *  non-zero error count. */
export const IMPORT_STATUS = Object.freeze({
  PROCESSING: 'Processing',
  COMPLETED: 'Completed',
  FAILED: 'Failed',
});

/**
 * The user type as the cell says it: lowercase, and plural unless exactly one.
 * targetUserRole arrives capitalised ('Mentee'), which reads wrong mid-sentence.
 */
export const userTypeLabel = (targetUserRole: string | undefined, count: number): string => {
  const role = (targetUserRole ?? '').trim().toLowerCase();
  if (!role) return '';
  return count === 1 ? role : `${role}s`;
};

/** "1 error" / "2 errors". */
export const errorLabel = (count: number): string => `${count} ${count === 1 ? 'error' : 'errors'}`;

/**
 * Only a settled import has a meaningful shortfall. For anything else the gap
 * between rows read and rows imported is just work that has not happened, so
 * calling it errors would invent failures the import never produced.
 */
const SETTLED: readonly string[] = [IMPORT_STATUS.COMPLETED, IMPORT_STATUS.FAILED];

export const isSettled = (status: string | undefined): boolean => SETTLED.includes(status ?? '');

/**
 * The API sends no error count, so it is the shortfall between the rows read
 * and the rows imported. Clamped at zero: a stored pair that disagrees should
 * render as no errors rather than as a negative tally.
 */
export const errorCountOf = (
  status: string | undefined,
  totalRows: number,
  totalImported: number | undefined
): number => (isSettled(status) ? Math.max(0, totalRows - (totalImported ?? 0)) : 0);
